import { useState, useEffect, useRef, useCallback, SyntheticEvent, MouseEvent } from 'react';

import ComplexTableProps, { Label, RowItemData } from './type';
import { Box } from '../../Box';
import Typography from '../../Typography';
import { SkeletonRow } from '../Rows';
import TablePagination from '../TablePagination';
import { useWindowSize } from '../../hooks';
import { getUniqueKey } from '../../utils';

import {
  EmptyTableRowsContainerStyled,
  EmptyTableRowStyled,
  HeaderStyled,
  HeaderDataContainerStyled,
  NoResultsFoundContainerStyled,
  TableContainerStyled,
  TableDataContainerStyled,
  TableContentContainerStyled,
} from './styled';

const ComplexTable = <T,>({ ...props }: ComplexTableProps<T>) => {
  const {
    data,
    emptyLabel,
    labels,
    total,
    rowsPerPage,
    page,
    notifyParent,
    RowController,
    fetchOnPageChange,
    rowControllerProps,
    rowsPerPageOptions,
    loading,
    style: tableStyleProp,
    ...rest
  } = props;

  const [rowItems, setRowItems] = useState<RowItemData>({});
  const [loadingData, setLoadingData] = useState<boolean>(loading);
  const [rowHeight, setRowHeight] = useState<number>(0);
  const { isMobile } = useWindowSize({ mobile: 700 });

  const tableRef = useRef<HTMLDivElement>(null);
  const tableContentRef = useRef<HTMLDivElement>(null);
  const tableHeaderRef = useRef<HTMLDivElement>(null);
  const minWidth = 75;

  const rowsPerPageCurrentOptions =
    rowsPerPageOptions?.length > 0 ? rowsPerPageOptions : [rowsPerPage];

  const getDefaultColumnWidth = useCallback(() => {
    const rawTableWidth = tableRef.current?.offsetWidth || 0;
    const totalFixedWidth = labels.reduce((sum, { width }) => sum + (width || 0), 0);
    const flexibleColumnsCount = labels.filter(({ width }) => !width).length;
    const tableWidth =
      rawTableWidth < totalFixedWidth
        ? totalFixedWidth + minWidth * flexibleColumnsCount
        : rawTableWidth;
    const remainingWidth = tableWidth - totalFixedWidth;
    if (flexibleColumnsCount === 0) return minWidth;
    const defaultWidth = remainingWidth / flexibleColumnsCount;
    return defaultWidth > minWidth - 24 ? defaultWidth : minWidth;
  }, [labels]);

  const getSliceToShow = (): [number, number] => {
    const pageLimitIndicator = rowsPerPage > total ? total : rowsPerPage;
    if (fetchOnPageChange) return [0, pageLimitIndicator];
    return [page * pageLimitIndicator, page * pageLimitIndicator + pageLimitIndicator];
  };

  const getEmptyRows = (): number => {
    const dataLimit = Math.min(data.length, total);
    if (fetchOnPageChange) return Math.max(0, rowsPerPage - dataLimit);
    return Math.max(0, Math.min(rowsPerPage, (1 + page) * rowsPerPage - dataLimit));
  };

  const handlePageChange = (__event: SyntheticEvent<HTMLButtonElement>, newPage: number) => {
    notifyParent({ type: 'changePage', data: { newPage, rowsPerPage } });
  };

  const handleRowsPerPageChange = (__event: MouseEvent<HTMLDivElement>, newRowsPerPage: number) => {
    notifyParent({ type: 'changeRowsPerPage', data: { newRowsPerPage } });
  };

  useEffect(() => {
    const defaultColumnWidth = getDefaultColumnWidth();
    setRowItems(
      labels.reduce((previous, { value: currentValue, width }) => {
        return {
          ...previous,
          [currentValue]: { value: currentValue, width: width || defaultColumnWidth },
        };
      }, {}),
    );
  }, [labels]);

  useEffect(() => {
    if (loading) setLoadingData(true);
  }, [loading]);

  useEffect(() => {
    if (!loading) setLoadingData(false);
  }, [data, loading]);

  useEffect(() => {
    if (tableContentRef.current && tableHeaderRef.current) {
      const contentHeight = tableContentRef.current.clientHeight || 0;
      const headerHeight = tableHeaderRef.current.clientHeight || 0;
      setRowHeight((contentHeight - headerHeight) / rowsPerPage);
    }
  }, [tableContentRef.current, tableHeaderRef.current, loading]);

  const renderLabels = () => {
    return (
      <HeaderStyled ref={tableHeaderRef}>
        {labels.map((label: Label) => {
          const { name, value, style = {} } = label || {};
          return (
            <HeaderDataContainerStyled
              key={value}
              width={rowItems[value]?.width}
              style={{ ...style }}
            >
              <Typography fontSize={14} style={{ lineHeight: 1.2 }}>
                {name}
              </Typography>
            </HeaderDataContainerStyled>
          );
        })}
      </HeaderStyled>
    );
  };

  const renderTableBody = () => {
    const emptyRows = getEmptyRows();
    if (loadingData) {
      return [...Array(rowsPerPage)].map((_, index) => (
        <SkeletonRow
          data-testid={`skeleton-row-${index}`}
          height={rowHeight}
          items={rowItems}
          key={getUniqueKey()}
        />
      ));
    }
    return (
      <div>
        {[...data].slice(...getSliceToShow()).map((rowItem) => {
          const itemId = getUniqueKey();
          return (
            <RowController
              key={itemId}
              data={rowItem}
              items={rowItems}
              notifyParent={notifyParent}
              rowControllerProps={rowControllerProps}
            />
          );
        })}
        {emptyRows > 0 && total !== 0 && (
          <EmptyTableRowStyled rowHeight={rowHeight} emptyRows={emptyRows}>
            <Box />
          </EmptyTableRowStyled>
        )}
      </div>
    );
  };
  return (
    <TableContainerStyled
      ref={tableRef}
      style={{
        ...(labels.filter(({ width }) => !width).length === 0 && !isMobile
          ? {
              width: `${labels.reduce((sum, { width }) => sum + (width || 0), 0)}px`,
            }
          : {}),
        ...tableStyleProp,
      }}
      {...rest}
    >
      <TableContentContainerStyled ref={tableContentRef}>
        <TableDataContainerStyled>
          {renderLabels()}
          {(data?.length !== 0 || loadingData) && renderTableBody()}
        </TableDataContainerStyled>
        {data?.length === 0 && !loadingData && (
          <EmptyTableRowsContainerStyled rowHeight={rowHeight} emptyRows={getEmptyRows()}>
            <Box>{emptyLabel}</Box>
          </EmptyTableRowsContainerStyled>
        )}
      </TableContentContainerStyled>
      {rowsPerPage < total && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageCurrentOptions}
          total={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          style={{ padding: '20px 24px 19px 24px' }}
          loading={loadingData}
        />
      )}
    </TableContainerStyled>
  );
};

ComplexTable.defaultProps = {
  data: null,
  emptyLabel: (
    <NoResultsFoundContainerStyled>
      <Typography variant="h5" weight="semi-bold">
        No encontramos resultados que coincidan con tu búsqueda.
      </Typography>
      <Typography>Prueba ajustando los filtros o ampliando los criterios</Typography>
    </NoResultsFoundContainerStyled>
  ),
  page: 0,
  total: 0,
  labels: [],
  rowsPerPage: 20,
  notifyParent: () => {},
  fetchOnPageChange: false,
  rowControllerProps: {},
  rowsPerPageOptions: [],
  loading: false,
};

export default ComplexTable;
